import { useMemo } from 'react';
import useGenericSimulator from '../../../hooks/useGenericSimulator';
import { panelPublicUrl } from '../../../../../panelurl';
import VisualizationControls from '../hud/VisualizationControls';
import './Monitor.scss';

interface GrafanaPanel {
    id: number;
    panel_name: string;
    panel_type: string;
    url: string;
}

const Monitor = () => {
    const { grafanaData, selectedElement } = useGenericSimulator();

    // Extract panels for the currently selected element
    const { panels } = useMemo<{ elementName: string; panels: GrafanaPanel[] }>(() => {
        if (!grafanaData || grafanaData.error || !selectedElement) {
            return { elementName: '', panels: [] };
        }
        const elementsPanelsUrls: any[] = grafanaData?.urls?.[0]?.elementsPanelsUrls ?? [];
        // Find the entry matching the selected element's id
        const match = elementsPanelsUrls.find((entry: any) => entry.elementId === selectedElement.elementId);
        if (!match) return { elementName: '', panels: [] };

        // The element name is the dynamic key (not 'elementId')
        const nameKey = Object.keys(match).find((k) => k !== 'elementId') || '';
        const panelList: GrafanaPanel[] = match[nameKey] ?? [];
        return { elementName: nameKey, panels: panelList };
    }, [grafanaData, selectedElement]);

    const placeholder = useMemo<{ title: string; message: string } | null>(() => {
        if (grafanaData === null) return { title: 'Loading...', message: 'Fetching monitoring data.' };
        if (grafanaData?.error) return { title: 'Error', message: 'Failed to load monitoring data.' };
        if (!selectedElement) return { title: 'No Element Selected', message: 'Select an element from the panel to view its telemetry.' };
        if (panels.length === 0) return { title: 'No Panels', message: `No monitoring panels available for ${selectedElement.elementName}.` };
        return null;
    }, [grafanaData, selectedElement, panels]);

    return (
        <div className='gs__monitor'>
            <VisualizationControls />
            {placeholder ? (
                <div className='gs__monitor__placeholder'>
                    <span>{placeholder.title}</span>
                    <p>{placeholder.message}</p>
                </div>
            ) : (
            <div className='gs__monitor__grid'>
                {panels.map((panel) => (
                    <div key={panel.id} className='gs__monitor__panel'>
                        <iframe
                            className='gs__monitor__panel-iframe'
                            sandbox='allow-scripts allow-same-origin'
                            src={`${panelPublicUrl}${panel.url}`}
                            title={panel.panel_name}
                            loading='lazy'
                        />
                    </div>
                ))}
            </div>
            )}
        </div>
    );
};

export default Monitor;
